import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link2, Check, Plus, Pencil, Trash2, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface PingPostProvider {
  id: string;
  name: string;
  url: string;
  secret_key: string;
  user_id: string;
  created_at?: string;
}

// URL validation function that allows query parameters
const isValidUrl = (url: string): boolean => {
  if (!url) return false; // Empty URLs are invalid for providers

  // Trim and check for spaces
  const trimmedUrl = url.trim();
  if (trimmedUrl !== url || trimmedUrl.includes(' ')) {
    return false;
  }

  try {
    // Use URL constructor for validation
    const urlObj = new URL(url);

    // Verify protocol is http or https
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return false;
    }

    const hostname = urlObj.hostname;

    // Make sure hostname doesn't have spaces
    if (hostname.includes(' ')) {
      return false;
    }

    // Check for double dots (invalid domain)
    if (hostname.includes('..')) {
      return false;
    }

    // Allow localhost and IP addresses for development
    if (hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        /^192\.168\./.test(hostname) ||
        /^10\./.test(hostname) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)) {
      return true;
    }

    // For other hostnames, ensure they have at least one dot (valid domain)
    if (!hostname.includes('.')) {
      return false;
    }

    return true;
  } catch (error) {
    // If URL constructor throws an error, the URL is invalid
    return false;
  }
};

export const GlobalIntegrations = () => {
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [providers, setProviders] = useState<PingPostProvider[]>([]);
  const [currentProvider, setCurrentProvider] = useState<PingPostProvider | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteProviderId, setDeleteProviderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadProviders();
    }
  }, [user]);

  const loadProviders = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('pingpost_providers')
        .select('*')
        .eq('user_id', user?.id);

      // Check if the error is due to the table not existing
      if (error && error.code === '42P01') { // PostgreSQL code for undefined_table
        console.log('The pingpost_providers table does not exist yet. Migration needs to be applied.');
        setProviders([]);
        setIsLoading(false);
        return;
      }

      if (error) throw error;

      setProviders(data || []);
    } catch (error: any) {
      console.error('Error loading pingpost providers:', error);
      toast({
        variant: 'destructive',
        title: 'Error loading providers',
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProvider = () => {
    setCurrentProvider({
      id: crypto.randomUUID(),
      name: '',
      url: '',
      secret_key: '',
      user_id: user?.id || ''
    });
    setIsEditing(false);
    setShowConfigModal(true);
  };

  const handleEditProvider = (provider: PingPostProvider) => {
    setCurrentProvider(provider);
    setIsEditing(true);
    setShowConfigModal(true);
  };

  const handleDeleteProvider = (id: string) => {
    setDeleteProviderId(id);
  };

  const confirmDeleteProvider = async () => {
    if (deleteProviderId) {
      try {
        const { error } = await supabase
          .from('pingpost_providers')
          .delete()
          .eq('id', deleteProviderId);

        if (error) throw error;

        setProviders(providers.filter(p => p.id !== deleteProviderId));

        toast({
          title: 'Provider deleted',
          description: 'The pingpost provider has been removed'
        });
      } catch (error: any) {
        toast({
          variant: 'destructive',
          title: 'Error deleting provider',
          description: error.message
        });
      } finally {
        setDeleteProviderId(null);
      }
    }
  };

  const handleSaveConfig = async () => {
    if (!currentProvider || !user) return;

    if (!currentProvider.name.trim()) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Provider name is required'
      });
      return;
    }

    if (!isValidUrl(currentProvider.url)) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Please enter a valid URL'
      });
      return;
    }

    if (!currentProvider.secret_key.trim()) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Secret key is required'
      });
      return;
    }

    try {
      if (isEditing) {
        // Update existing provider
        const { error } = await supabase
          .from('pingpost_providers')
          .update({
            name: currentProvider.name,
            url: currentProvider.url,
            secret_key: currentProvider.secret_key
          })
          .eq('id', currentProvider.id);

        if (error) throw error;

        setProviders(providers.map(p =>
          p.id === currentProvider.id ? currentProvider : p
        ));

        toast({
          title: 'Provider updated',
          description: 'The pingpost provider has been updated successfully'
        });
      } else {
        // Add new provider
        const { error } = await supabase
          .from('pingpost_providers')
          .insert([{
            id: currentProvider.id,
            name: currentProvider.name,
            url: currentProvider.url,
            secret_key: currentProvider.secret_key,
            user_id: user.id
          }]);

        if (error) throw error;

        setProviders([...providers, currentProvider]);

        toast({
          title: 'Provider added',
          description: 'The pingpost provider has been added successfully'
        });
      }

      setShowConfigModal(false);
      setCurrentProvider(null);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error saving provider',
        description: error.message
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold">Global PingPost Providers</h4>
                <p className="text-sm text-gray-600">
                  Configure global PingPost providers to send lead data to multiple external endpoints.
                  These providers can be enabled or disabled for each chatbot.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {isLoading ? (
                <div className="text-sm text-gray-600">Loading providers...</div>
              ) : providers.length === 0 ? (
                <div className="space-y-2">
                  <div className="text-sm text-gray-600">No providers configured yet.</div>
                 
                </div>
              ) : (
                providers.map(provider => (
                  <div
                    key={provider.id}
                    className="flex items-center justify-between p-3 border rounded-md"
                  >
                    <div className="space-y-1">
                      <div className="font-medium flex items-center gap-2">
                        {provider.name}
                        <Badge variant="outline" className="text-xs">
                          Global
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <Link2 className="h-3 w-3" />
                        {provider.url}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditProvider(provider)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteProvider(provider.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleAddProvider}
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Add Global Provider
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Dialog
        open={showConfigModal}
        onOpenChange={(open) => {
          setShowConfigModal(open);
          if (!open) setCurrentProvider(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit' : 'Add'} Global PingPost Provider</DialogTitle>
            <DialogDescription>
              Configure a global ping post provider to send lead data to external endpoints.
              This provider can be enabled or disabled for each chatbot.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Provider Name</Label>
              <Input
                id="name"
                placeholder="My Lead Provider"
                value={currentProvider?.name || ''}
                onChange={(e) => setCurrentProvider(prev =>
                  prev ? {...prev, name: e.target.value} : null
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">Endpoint URL</Label>
              <div className="relative">
                <Input
                  id="url"
                  placeholder="https://your-endpoint.com/api"
                  value={currentProvider?.url || ''}
                  onChange={(e) => setCurrentProvider(prev =>
                    prev ? {...prev, url: e.target.value} : null
                  )}
                  className={currentProvider?.url && !isValidUrl(currentProvider.url) ? 'border-red-300 pr-10' : ''}
                />
                {currentProvider?.url && !isValidUrl(currentProvider.url) && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  </div>
                )}
              </div>
              {currentProvider?.url && !isValidUrl(currentProvider.url) && (
                <p className="text-xs text-red-500 mt-1">
                  Please enter a valid URL (must start with http:// or https://)
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="secretKey">Secret Key</Label>
              <Input
                id="secretKey"
                type="password"
                placeholder="Enter your secret key"
                value={currentProvider?.secret_key || ''}
                onChange={(e) => setCurrentProvider(prev =>
                  prev ? {...prev, secret_key: e.target.value} : null
                )}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig}>Save Provider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteProviderId} onOpenChange={(open) => !open && setDeleteProviderId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this global ping post provider. This action cannot be undone.
              Any chatbots using this provider will no longer be able to send data to this endpoint.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteProvider}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
